import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock, CheckCircle2, XCircle, LogOut, ArrowRight, Home } from "lucide-react";

export default function CoachPending() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/coach/status").then((r) => setStatus(r.data));
  }, []);

  const cs = status?.coach_status || user?.coach_status;

  if (cs === "approved") {
    nav("/dashboard");
    return null;
  }

  const handleLogout = async () => { await logout(); nav("/login"); };

  let icon = Clock, color = "#FF4500", title = "Review in progress", body = "An admin is reviewing your certification and insurance. This usually takes under 48h. We'll notify you when your account is approved.";
  if (cs === "rejected") {
    icon = XCircle; color = "#FF4500"; title = "Application not approved"; body = status?.review?.reason || "Please contact support for next steps.";
  }
  if (cs === "approved") { icon = CheckCircle2; color = "#CCFF00"; title = "Approved!"; body = "You're cleared to coach. Head to your dashboard."; }

  const Icon = icon;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        <div className="card-surface p-10 text-center" data-testid="coach-pending">
          <Icon className="w-16 h-16 mx-auto mb-6" style={{ color }} strokeWidth={1.5} />
          <div className="label-mono mb-2" style={{ color }}>STATUS · {(cs || "INCOMPLETE").toUpperCase()}</div>
          <h1 className="h-display text-4xl tracking-tighter mb-4">{title}</h1>
          <p className="text-[#A3A3A3] mb-8 max-w-md mx-auto leading-relaxed">{body}</p>

          {status?.coach_application && (
            <div className="text-left text-sm border border-[#262626] p-4 space-y-1 mb-6">
              <div className="label-mono">APPLICATION SUMMARY</div>
              <Row k="Certification" v={status.coach_application.certification} />
              <Row k="Awarding body" v={status.coach_application.cert_body} />
              <Row k="Insurance" v={`${status.coach_application.insurance_provider} · #${status.coach_application.insurance_policy_number}`} />
              <Row k="Policy expiry" v={status.coach_application.insurance_expiry} />
              <Row k="Experience" v={`${status.coach_application.years_experience} years`} />
            </div>
          )}

          <div className="flex items-center justify-center gap-4 flex-wrap">
            {cs === "incomplete" && (
              <Link to="/coach/apply" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="continue-application">
                Submit application <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
            <Link to="/" data-testid="pending-back-home" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5">
              <Home className="w-3.5 h-3.5" /> Back to home
            </Link>
            <button onClick={handleLogout} data-testid="pending-signout" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-[#262626] last:border-0">
      <span className="text-[#A3A3A3]">{k}</span>
      <span className="text-white text-right">{v || "—"}</span>
    </div>
  );
}
